import { Button, Card, Col, Drawer, Form, Input, Row, Space, Spin, message } from "antd"
import { WarehouseContext } from "./"
import { useContext, useEffect, useState } from "react"
import { EnvironmentOutlined } from "@ant-design/icons"

import { MapContainer, Marker, TileLayer, useMapEvents } from "react-leaflet"
import L from "leaflet"
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png"
import markerIcon from "leaflet/dist/images/marker-icon.png"
import markerShadow from "leaflet/dist/images/marker-shadow.png"
import { useMap } from "react-leaflet"

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 } // India
const DEFAULT_ZOOM = 13
const FOCUS_ZOOM = 16

L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
})

const OSM_MARKER_ICON = new L.Icon({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
})

const AddWarehouse = () => {

    const { form, onFinish, addModalOpen, onClose, handleSubmit, warehouseData, setIsEditMode } = useContext(WarehouseContext)

    const [loading, setLoading] = useState(false)
    const [mapPosition, setMapPosition] = useState(DEFAULT_CENTER)
    const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM)

    const parseNumber = (value) => {
        const n = Number(value)
        return Number.isFinite(n) ? n : null
    }

    const reverseGeocodeAndFill = async (latitude, longitude) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
                    latitude
                )}&lon=${encodeURIComponent(longitude)}`
            )
            const data = await response.json()

            const address = data?.address || {}
            const city = address.city || address.town || address.village || address.county || ""
            const state = address.state || ""
            const country = address.country || ""
            const pincode = address.postcode || ""
            const formattedAddress = data?.display_name || ""

            form.setFieldsValue({
                latitude,
                longitude,
                address: formattedAddress || form.getFieldValue("address"),
                city: city || form.getFieldValue("city"),
                state: state || form.getFieldValue("state"),
                country: country || form.getFieldValue("country"),
                pincode: pincode || form.getFieldValue("pincode"),
            })
        } catch (error) {
            message.error("Error fetching address data.")
        }
    }

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            setLoading(true)
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords
                    setMapPosition({ lat: latitude, lng: longitude })
                    setMapZoom(FOCUS_ZOOM)
                    await reverseGeocodeAndFill(latitude, longitude)
                    message.success("Address auto-filled successfully!")
                    setLoading(false)
                },
                (error) => {
                    message.error("Location access denied. Enable GPS & try again.")
                    setLoading(false)
                }
            )
        } else {
            message.error("Geolocation is not supported in this browser.")
        }
    }

    useEffect(() => {
        if (!addModalOpen) return
        const lat = parseNumber(form.getFieldValue("latitude"))
        const lng = parseNumber(form.getFieldValue("longitude"))
        if (lat !== null && lng !== null) setMapPosition({ lat, lng })
    }, [addModalOpen])

    const MapClickHandler = ({ onPick }) => {
        useMapEvents({
            click: (e) => onPick(e.latlng),
        })
        return null
    }

    const MapInvalidateOnOpen = ({ trigger }) => {
        const map = useMap()
        useEffect(() => {
            // Drawer animation/layout can cause initial wrong size → grey tiles.
            const t = setTimeout(() => {
                try {
                    map.invalidateSize()
                } catch { }
            }, 250)
            return () => clearTimeout(t)
        }, [map, trigger])
        return null
    }

    const MapFlyTo = ({ position, zoom }) => {
        const map = useMap()
        useEffect(() => {
            if (!position) return
            const t = setTimeout(() => {
                try {
                    map.flyTo(position, zoom ?? map.getZoom(), { animate: true, duration: 0.6 })
                } catch { }
            }, 50)
            return () => clearTimeout(t)
        }, [map, position, zoom])
        return null
    }

    return (
        <Drawer
            title="Create a new Warehouses"
            width={720}
            onClose={onClose}
            open={addModalOpen}
            styles={{ body: { paddingBottom: 80 } }}
            extra={
                <Space>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" onClick={handleSubmit}>
                        Submit
                    </Button>
                </Space>
            }
        >
            <Card>
                <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
                    {/* Other form fields */}

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Warehouse Name"
                                name="name"
                                rules={[{ required: true, message: "Please enter warehouse name" }]}
                            >
                                <Input placeholder="Enter warehouse name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Address"
                                name="address"
                                rules={[{ required: true, message: "Please enter address" }]}
                            >
                                <Input placeholder="Enter street address" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="City"
                                name="city"
                                rules={[{ required: true, message: "Please enter city" }]}
                            >
                                <Input placeholder="Enter city name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="State"
                                name="state"
                                rules={[{ required: true, message: "Please enter state" }]}
                            >
                                <Input placeholder="Enter state name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Country"
                                name="country"
                                rules={[{ required: true, message: "Please enter country" }]}
                            >
                                <Input placeholder="Enter country name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Pincode"
                                name="pincode"
                                rules={[
                                    { required: true, message: "Please enter pincode" },
                                    { pattern: /^[0-9]{5,6}$/, message: "Invalid pincode format" }
                                ]}
                            >
                                <Input placeholder="Enter pincode" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Manager"
                                name="manager"
                                rules={[{ required: true, message: "Please enter manager name" }]}
                            >
                                <Input placeholder="Enter manager name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Contact Phone"
                                name="contactPhone"
                                rules={[
                                    { required: true, message: "Please enter contact phone" },
                                    { pattern: /^\+?[0-9]{10,15}$/, message: "Invalid phone number format" }
                                ]}
                            >
                                <Input placeholder="Enter contact phone" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Latitude"
                                name="latitude"
                                rules={[{ required: true, message: "Please enter latitude" }]}
                            >
                                <Input
                                    placeholder="Enter latitude (e.g., 21.5724)"
                                    onBlur={() => {
                                        const lat = parseNumber(form.getFieldValue("latitude"))
                                        const lng = parseNumber(form.getFieldValue("longitude"))
                                        if (lat !== null && lng !== null) setMapPosition({ lat, lng })
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Longitude"
                                name="longitude"
                                rules={[{ required: true, message: "Please enter longitude" }]}
                            >
                                <Input
                                    placeholder="Enter longitude (e.g., 72.90)"
                                    onBlur={() => {
                                        const lat = parseNumber(form.getFieldValue("latitude"))
                                        const lng = parseNumber(form.getFieldValue("longitude"))
                                        if (lat !== null && lng !== null) setMapPosition({ lat, lng })
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Col span={12}>
                        <Button type="primary" onClick={getCurrentLocation} block disabled={loading} icon={<EnvironmentOutlined />} >
                            {loading ? <Spin /> : "Use current location"}
                        </Button>
                    </Col>

                    <div style={{ marginTop: 16 }}>
                        <Card size="small" title="Select location on map (OpenStreetMap)">
                            <div style={{ height: 320, width: "100%" }}>
                                <MapContainer center={mapPosition} zoom={mapZoom} style={{ height: "100%", width: "100%" }}>
                                    <MapInvalidateOnOpen trigger={addModalOpen} />
                                    <MapFlyTo position={mapPosition} zoom={mapZoom} />
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <MapClickHandler
                                        onPick={async (latlng) => {
                                            const next = { lat: latlng.lat, lng: latlng.lng }
                                            setMapPosition(next)
                                            setMapZoom(FOCUS_ZOOM)
                                            await reverseGeocodeAndFill(latlng.lat, latlng.lng)
                                        }}
                                    />
                                    <Marker
                                        position={mapPosition}
                                        icon={OSM_MARKER_ICON}
                                        draggable
                                        eventHandlers={{
                                            dragend: async (e) => {
                                                const marker = e.target
                                                const latlng = marker.getLatLng()
                                                const next = { lat: latlng.lat, lng: latlng.lng }
                                                setMapPosition(next)
                                                setMapZoom(FOCUS_ZOOM)
                                                await reverseGeocodeAndFill(latlng.lat, latlng.lng)
                                            },
                                        }}
                                    />
                                </MapContainer>
                            </div>
                        </Card>
                    </div>
                </Form>
            </Card>


        </Drawer>
    )
}
export default AddWarehouse